import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteE } from 'src/constants/pages.constants';
import { FAQ_ITEMS } from 'src/features/common/faq/containers/constants';
import { ILocale } from 'src/features/common/faq/containers/highlighted-questions/types';
import { useUniverse } from 'src/features/common/universe/hooks/use-universe';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { LandingSection } from 'src/ui/common/layouts/landing-section';
import { Accordion } from 'src/ui/common/molecules/accordion';
import { StyledList, StyledListStep, StyledParagraph } from '../faq-container.styles';

export const HighlightedQuestions: React.VFC<{ title?: string }> = ({ title }) => {
    const { t } = useTranslation(['pages']);
    const { projectName } = useUniverse();

    const locale: ILocale = useMemo(
        () => ({
            ...t('faq', { returnObjects: true, faqCompanyName: projectName }),
        }),
        [t, projectName]
    );

    return (
        <LandingSection seeMoreLink={RouteE.FAQ} title={title ?? locale.landingTitle}>
            <Accordion>
                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                    <Typography variant="text" sizeSm="base" color="white">
                        {locale?.whatIsCompany?.title}
                    </Typography>
                </Accordion.ItemHeader>
                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_COMPANY}>
                    <StyledParagraph>{locale?.whatIsCompany?.paragraph}</StyledParagraph>
                </Accordion.ItemContent>
                <Accordion.ItemHeader itemId={FAQ_ITEMS.HOW_TO_START}>
                    <Typography variant="text" sizeSm="base" color="white">
                        {locale?.howToStart?.title}
                    </Typography>
                </Accordion.ItemHeader>
                <Accordion.ItemContent itemId={FAQ_ITEMS.HOW_TO_START}>
                    <StyledListStep>
                        {locale?.howToStart?.paragraphs?.map((item) => (
                            <li key={item}>{item}</li>
                        ))}
                    </StyledListStep>
                </Accordion.ItemContent>
                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                    <Typography variant="text" sizeSm="base" color="white">
                        {locale?.whatIsNFT?.title}
                    </Typography>
                </Accordion.ItemHeader>
                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_NFT}>
                    <Spacing pr="20px">
                        {locale?.whatIsNFT?.paragraphs?.map((item) => (
                            <StyledParagraph key={item}>{item}</StyledParagraph>
                        ))}
                        <StyledList>
                            {locale?.whatIsNFT?.fungibleItems?.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </StyledList>
                        <StyledParagraph>{locale?.whatIsNFT?.nonFungible}</StyledParagraph>
                        <StyledList>
                            {locale?.whatIsNFT?.nonFungibleItems?.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </StyledList>
                        <StyledParagraph>{locale?.whatIsNFT?.publiclyOwnership}</StyledParagraph>
                        <StyledList>
                            {locale?.whatIsNFT?.publiclyOwnershipItems?.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </StyledList>
                    </Spacing>
                </Accordion.ItemContent>
                <Accordion.ItemHeader itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                    <Typography variant="text" sizeSm="base" color="white">
                        {locale?.whatIsMinting?.title}
                    </Typography>
                </Accordion.ItemHeader>
                <Accordion.ItemContent itemId={FAQ_ITEMS.WHAT_IS_MINTING}>
                    {locale?.whatIsMinting?.paragraphs?.map((item) => (
                        <StyledParagraph key={item}>{item}</StyledParagraph>
                    ))}
                </Accordion.ItemContent>
            </Accordion>
        </LandingSection>
    );
};
